<template>
  <multiselect-dropdown
    :key="`${selectedStep?.id}-${selectedStep?.type}`"
    :value="selectedContracts"
    :options="contractOptions"
    :disabled="areContractsLoading"
    :placeholder="$t('CONTRACTS.CONTRACT_TEMPLATE', 2)"
    data-cy="contracts-step-templates-dropdown"
    @change="handleSelectContract"
  />
  <lf-dropdown
    :key="`${selectedStep?.id}-${selectedStep?.type}`"
    :value="selectedOwnerOptionLabel"
    :options="options?.definition.owners_required"
    :placeholder="$t('CHECKOUT.NUMBER_OF_OWNERS')"
    full-width
    data-cy="contracts-step-owners-dropdown"
    @change="handleSelectRequiredOwner"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { checkoutBaseToDedicatedStep } from "@/helpers/checkout";
import { useContractsIndexQuery } from "@/hooks/queries/contracts";
import { useCheckoutOptionsQuery } from "@/hooks/queries/options";
import { ContractStatus } from "@/models/contractBuilder";
import { useCheckoutStore } from "@/stores/checkout";

import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import MultiselectDropdown from "@/components/ui/inputs/MultiselectDropdown.vue";

import type { MultiSelectOption } from "@/models/options";

const { t } = useI18n();

const { selectedStep, activeTemplateClient } = storeToRefs(useCheckoutStore());

const contractsStep = computed(() =>
  checkoutBaseToDedicatedStep(selectedStep.value, "contracts")
);

const { data: options } = useCheckoutOptionsQuery();

const { data: contracts, isLoading: areContractsLoading } =
  useContractsIndexQuery();

const selectedOwnerOption = ref(
  contractsStep.value?.templates?.[0]?.owners_required ?? "all"
);

const contractOptions = computed<MultiSelectOption[]>(
  () =>
    contracts.value?.data
      .filter(
        (contract) =>
          contract.status === ContractStatus.published &&
          (contract.client?.id === activeTemplateClient.value?.id ||
            contract.is_lendflow_template)
      )
      .map((template) => ({
        id: String(template.id),
        name: template.name,
      })) ?? []
);

// The multiselect dropdown only accepts objects with string ids
const selectedContracts = computed(
  () =>
    contractsStep.value?.templates?.map((template) => ({
      id: String(template.id),
    })) ?? []
);

type OwnerOption = "all" | "primary" | "any";

const selectedOwnerOptionLabel = computed(() => {
  const labels: Record<OwnerOption, string> = {
    all: t("CHECKOUT.OWNER_OPTIONS.ALL_OWNERS"),
    primary: t("CHECKOUT.OWNER_OPTIONS.PRIMARY_OWNER"),
    any: t("CHECKOUT.OWNER_OPTIONS.PRIMARY_OWNER"),
  };
  return (
    labels[selectedOwnerOption.value as OwnerOption] ??
    selectedOwnerOption.value
  );
});

const handleSelectContract = (value: Pick<MultiSelectOption, "id">[]) => {
  if (!contractsStep.value) {
    return;
  }

  if (!contractsStep.value.templates) {
    contractsStep.value.templates = [];
  }

  contractsStep.value.templates = value.map((value) => ({
    id: Number(value.id),
    owners_required: selectedOwnerOption.value,
  }));
};

const handleSelectRequiredOwner = (value: string | number) => {
  selectedOwnerOption.value = value as string;
};

watch(
  selectedOwnerOption,
  (newSelectedOwnerOption) => {
    contractsStep.value?.templates?.forEach((template) => {
      template.owners_required = newSelectedOwnerOption;
    });
  },
  { immediate: true }
);
</script>
